import React, { useCallback, useEffect, useState } from "react";
import { useApp, apiFetch } from "../../context/AppContext";
import { sortDepartmentsByCode } from "../../hooks/useDepartments";
import { confirmAction } from "../../utils/sweetAlert";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  DashboardStatCard,
  Pagination,
} from "../../components/common/UI";
import {
  Network,
  Search,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  User,
  Hash,
  ListPlus,
  CheckCircle2,
  XCircle,
  Users,
  Mail,
  Crown,
} from "lucide-react";

const _fetch =
  typeof apiFetch === "function"
    ? apiFetch
    : (path, opts) =>
        fetch(`/api/v1${path}`, {
          headers: { "Content-Type": "application/json" },
          ...opts,
        });

const EMPTY_FORM = { name: "", owner_name: "", email: "" };

const BULK_PLACEHOLDER = `Human Resources, Jane Smith, jane@yourcompany.com
Finance, John Doe, john@yourcompany.com
Information Technology | Alex Kumar | alex@yourcompany.com
Legal, Priya Sharma`;

/** Parse bulk textarea: one department per line as "Name, Owner, Email" or "Name | Owner | Email". */
function parseBulkDepartmentLines(text) {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const rows = [];
  const lineErrors = [];
  const seenNames = new Map();

  lines.forEach((line, index) => {
    const parts = line.split(/[,|]/).map((p) => p.trim());
    if (parts.length < 2) {
      lineErrors.push(
        `Line ${index + 1}: use "Name, Owner" or "Name, Owner, Email"`,
      );
      return;
    }
    const name = parts[0];
    const owner_name = parts[1];
    const email = parts[2] || "";
    if (!name || !owner_name) {
      lineErrors.push(`Line ${index + 1}: name and owner are required`);
      return;
    }
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      lineErrors.push(`Line ${index + 1}: invalid email format`);
      return;
    }
    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey)) {
      lineErrors.push(
        `Line ${index + 1}: "${name}" is already listed on line ${seenNames.get(nameKey)}`,
      );
      return;
    }
    seenNames.set(nameKey, index + 1);
    rows.push({ name, owner_name, email: email || null });
  });

  return { rows, lineErrors };
}

export default function DepartmentsPage() {
  const { showNotification } = useApp();
  const [departments, setDepartments] = useState([]);
  const [deptStats, setDeptStats] = useState({
    total: 0,
    active_count: 0,
    inactive_count: 0,
    owners_count: 0,
  });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkErrors, setBulkErrors] = useState([]);
  const [editDept, setEditDept] = useState(null);
  const [deleteDept, setDeleteDept] = useState(null);
  const [purgeDept, setPurgeDept] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [purging, setPurging] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        include_inactive: "true",
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (search.trim()) params.set("search", search.trim());
      const res = await _fetch(`/tenant/departments?${params}`);
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to load departments", "error");
        return;
      }
      setDepartments(sortDepartmentsByCode(data.items || []));
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setDeptStats({
        total: data.total || 0,
        active_count: data.active_count ?? 0,
        inactive_count: data.inactive_count ?? 0,
        owners_count: data.owners_count ?? 0,
      });
    } catch {
      showNotification("Network error", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, showNotification]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchDepartments();
    }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.owner_name.trim()) e.owner_name = "Required";
    if (
      form.email &&
      form.email.trim() &&
      !/\S+@\S+\.\S+/.test(form.email.trim())
    ) {
      e.email = "Invalid email format";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await _fetch("/tenant/departments", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          owner_name: form.owner_name.trim(),
          email: form.email.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to create department", "error");
        return;
      }
      showNotification(`Department "${data.name}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setErrors({});
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkCreate = async () => {
    const { rows, lineErrors } = parseBulkDepartmentLines(bulkText);
    setBulkErrors(lineErrors);
    if (lineErrors.length) return;
    if (!rows.length) {
      setBulkErrors(["Enter at least one department (one per line)."]);
      return;
    }

    setSaving(true);
    try {
      const res = await _fetch("/tenant/departments/bulk", {
        method: "POST",
        body: JSON.stringify({ departments: rows }),
      });
      const data = await res.json();
      if (!res.ok) {
        const message = data.detail || "Failed to create departments";
        setBulkErrors([typeof message === "string" ? message : "Failed to create departments"]);
        showNotification(
          typeof message === "string" ? message : "Failed to create departments",
          "error",
        );
        return;
      }
      showNotification(
        `${data.created} department${data.created === 1 ? "" : "s"} created`,
      );
      setShowBulk(false);
      setBulkText("");
      setBulkErrors([]);
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (dept) => {
    setEditDept(dept);
    setForm({
      name: dept.name || "",
      owner_name: dept.owner_name || "",
      email: dept.email || "",
    });
    setErrors({});
  };

  const handleEdit = async () => {
    if (!validate() || !editDept) return;
    setSaving(true);
    try {
      const res = await _fetch(`/tenant/departments/${editDept.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: form.name.trim(),
          owner_name: form.owner_name.trim(),
          email: form.email.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to update department", "error");
        return;
      }
      showNotification("Department updated");
      setEditDept(null);
      setForm(EMPTY_FORM);
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteDept) return;
    setDeleting(true);
    try {
      const res = await _fetch(`/tenant/departments/${deleteDept.id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(
          data.detail || "Failed to deactivate department",
          "error",
        );
        return;
      }
      showNotification(`Department "${deleteDept.name}" deactivated`);
      setDeleteDept(null);
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleActivate = async (dept) => {
    setActivatingId(dept.id);
    try {
      const res = await _fetch(`/tenant/departments/${dept.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification(
          data.detail || "Failed to activate department",
          "error",
        );
        return;
      }
      showNotification(`Department "${dept.name}" activated`);
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setActivatingId(null);
    }
  };

  const handleSetHeadDepartment = async (departmentId) => {
    const confirmed = await confirmAction({
      title: "Set Head Department?",
      text:
        "Only one department can be Head Department. The SPOC of that department becomes SPOC Head with access across all departments. Any existing delegate grants on the previous Head Department will be revoked.",
      confirmText: "Continue",
    });
    if (!confirmed) return;

    const res = await apiFetch(`/head-department/flag/${departmentId}`, {
      method: "PATCH",
      body: JSON.stringify({ is_head: true }),
    });
    if (res.ok) {
      showNotification("Head Department updated");
      fetchDepartments();
    } else {
      const data = await res.json().catch(() => ({}));
      showNotification(data.detail || "Failed to update Head Department", "error");
    }
  };

  const handlePermanentDelete = async () => {
    if (!purgeDept) return;
    setPurging(true);
    try {
      const res = await _fetch(
        `/tenant/departments/${purgeDept.id}?permanent=true`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok) {
        showNotification(data.detail || "Failed to delete department", "error");
        return;
      }
      showNotification(`Department "${purgeDept.name}" permanently deleted`);
      setPurgeDept(null);
      fetchDepartments();
    } catch {
      showNotification("Network error", "error");
    } finally {
      setPurging(false);
    }
  };

  const stats = [
    {
      label: "Total Departments",
      value: deptStats.total,
      sub: "organisation units",
      icon: Network,
      iconClass: "from-teal-400 to-cyan-600",
    },
    {
      label: "Active",
      value: deptStats.active_count,
      sub: "currently enabled",
      icon: CheckCircle2,
      color: "emerald",
    },
    {
      label: "Inactive",
      value: deptStats.inactive_count,
      sub: "deactivated",
      icon: XCircle,
      accent: "#94a3b8",
    },
    {
      label: "Owners",
      value: deptStats.owners_count,
      sub: "unique department heads",
      icon: Users,
      color: "violet",
    },
  ];

  const bulkPreview = parseBulkDepartmentLines(bulkText).rows;

  const createFormFields = (
    <div className="space-y-4">
      <Input
        label="Department Name"
        placeholder="e.g. Human Resources"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        error={errors.name}
        leftElement={<Network size={14} />}
      />
      <Input
        label="Owner Name"
        placeholder="Department head or owner"
        value={form.owner_name}
        onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
        error={errors.owner_name}
        leftElement={<User size={14} />}
      />
      <Input
        label="Owner Email"
        placeholder="owner@organization.com"
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        error={errors.email}
        leftElement={<Mail size={14} />}
      />
      <p className="text-xs text-slate-400">
        Department ID is assigned automatically. Users pick a department when
        submitting assessments.
      </p>
    </div>
  );

  const editFormFields = (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Department ID
        </label>
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-mono font-semibold text-slate-700">
          <Hash size={14} className="text-slate-400 shrink-0" />
          {editDept?.code ?? "—"}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Auto-generated — cannot be changed
        </p>
      </div>
      <Input
        label="Department Name"
        placeholder="e.g. Human Resources"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        error={errors.name}
        leftElement={<Network size={14} />}
      />
      <Input
        label="Owner Name"
        placeholder="Department head or owner"
        value={form.owner_name}
        onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
        error={errors.owner_name}
        leftElement={<User size={14} />}
      />
      <Input
        label="Owner Email"
        placeholder="owner@organization.com"
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        error={errors.email}
        leftElement={<Mail size={14} />}
      />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div
        data-tour-id="tour-dept-header"
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-md flex-shrink-0">
            <Network size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Departments</h1>
            <p className="text-sm text-slate-500 mt-1">
              Add your organisation&apos;s departments — users choose one when
              filling assessments
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            data-tour-id="tour-dept-bulk"
            leftIcon={<ListPlus size={15} />}
            onClick={() => setShowBulk(true)}
          >
            Add Multiple
          </Button>
          <Button
            data-tour-id="tour-add-department"
            leftIcon={<Plus size={15} />}
            onClick={() => setShowCreate(true)}
          >
            Add One
          </Button>
        </div>
      </div>

      <div
        data-tour-id="tour-dept-stats"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3"
      >
        {stats.map((s) => (
          <DashboardStatCard key={s.label} {...s} loading={loading} />
        ))}
      </div>

      <Card padding={false}>
        <div
          data-tour-id="tour-dept-search"
          className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-4 border-b border-slate-100"
        >
          <div className="relative flex-1 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, ID, or owner..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={fetchDepartments}
            disabled={loading}
            className="p-2 rounded-xl text-slate-400 hover:text-teal-600 hover:bg-teal-50 border border-slate-200"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <div data-tour-id="tour-dept-table">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
              <RefreshCw size={14} className="animate-spin" /> Loading
              departments...
            </div>
          ) : departments.length === 0 ? (
            <div className="py-16">
              <EmptyState
                icon={<Network size={44} className="text-teal-500" />}
                title="No departments yet"
                description="Add departments so users can select them when filling Gap, TPRM, PIA, and ROPA forms"
                action={
                  <div className="flex flex-wrap gap-2 justify-center">
                    <Button
                      variant="secondary"
                      leftIcon={<ListPlus size={14} />}
                      onClick={() => setShowBulk(true)}
                    >
                      Add Multiple
                    </Button>
                    <Button
                      leftIcon={<Plus size={14} />}
                      onClick={() => setShowCreate(true)}
                    >
                      Add One
                    </Button>
                  </div>
                }
              />
            </div>
          ) : (
            <>
              <div className="w-full max-w-full overflow-auto scrollbar-thin max-h-[calc(3.5rem+5*3.25rem)]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm">
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Department ID
                      </th>
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Department Name
                      </th>
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Head
                      </th>
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Owner
                      </th>
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Email
                      </th>
                      <th className="px-5 py-3 font-semibold bg-slate-50/95">
                        Status
                      </th>
                      <th className="px-5 py-3 font-semibold text-right bg-slate-50/95">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept) => (
                      <tr
                        key={dept.id}
                        className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors"
                      >
                        <td className="px-5 py-3 font-mono text-xs font-semibold text-slate-700">
                          {dept.code}
                        </td>
                        <td className="px-5 py-3 font-medium text-slate-900">
                          {dept.name}
                        </td>
                        <td className="px-5 py-3">
                          {dept.is_head ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-1 text-[11px] font-semibold">
                              <Crown size={12} />
                              Head Department
                            </span>
                          ) : dept.is_active ? (
                            <button
                              type="button"
                              onClick={() => handleSetHeadDepartment(dept.id)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg px-2 py-1 transition-colors"
                              title="Mark as the organisation Head Department"
                            >
                              <Crown size={12} className="opacity-60" />
                              Set as Head
                            </button>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          {dept.owner_name}
                        </td>
                        <td className="px-5 py-3 text-slate-600 font-mono text-xs">
                          {dept.email || "—"}
                        </td>
                        <td className="px-5 py-3">
                          <Badge
                            variant={dept.is_active ? "success" : "inactive"}
                          >
                            {dept.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openEdit(dept)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100"
                            >
                              <Edit2 size={12} /> Edit
                            </button>
                            {dept.is_active ? (
                              <button
                                type="button"
                                onClick={() => setDeleteDept(dept)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100"
                              >
                                <XCircle size={12} /> Deactivate
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleActivate(dept)}
                                disabled={activatingId === dept.id}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50"
                              >
                                <CheckCircle2 size={12} />{" "}
                                {activatingId === dept.id
                                  ? "Activating…"
                                  : "Activate"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setPurgeDept(dept)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 pb-4 border-t border-slate-100">
                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                  itemLabel="departments"
                />
              </div>
            </>
          )}
        </div>
      </Card>

      <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3 text-sm text-amber-950/80 leading-relaxed">
        <p className="font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
          <Crown size={14} />
          Head Department
        </p>
        <p className="text-xs text-amber-900/70">
          Mark exactly one department as Head. The SPOC of that department
          (set on the user form) becomes SPOC Head with organisation-wide
          access. They can temporarily delegate that access to other SPOCs
          from the same user form — without changing the delegate’s permanent
          department or role.
        </p>
      </div>

      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setForm(EMPTY_FORM);
          setErrors({});
        }}
        title="Add Department"
      >
        {createFormFields}
        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              setShowCreate(false);
              setForm(EMPTY_FORM);
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleCreate}
            loading={saving}
            leftIcon={<Plus size={14} />}
          >
            Create
          </Button>
        </div>
      </Modal>

      <Modal
        open={showBulk}
        onClose={() => {
          setShowBulk(false);
          setBulkText("");
          setBulkErrors([]);
        }}
        title="Add Multiple Departments"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Enter one department per line. Use a comma or pipe between department,
            owner, and email (optional). IDs are assigned automatically (1, 2,
            3…).
          </p>
          <textarea
            value={bulkText}
            onChange={(e) => {
              setBulkText(e.target.value);
              setBulkErrors([]);
            }}
            placeholder={BULK_PLACEHOLDER}
            rows={10}
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[180px]"
          />
          {bulkErrors.length > 0 && (
            <ul className="text-xs text-red-600 space-y-1">
              {bulkErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          )}
          {bulkPreview.length > 0 && bulkErrors.length === 0 && (
            <p className="text-xs text-teal-700 font-medium">
              Ready to create {bulkPreview.length} department
              {bulkPreview.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => {
              setShowBulk(false);
              setBulkText("");
              setBulkErrors([]);
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleBulkCreate}
            loading={saving}
            leftIcon={<ListPlus size={14} />}
          >
            Create All
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!editDept}
        onClose={() => setEditDept(null)}
        title="Edit Department"
      >
        {editFormFields}
        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => setEditDept(null)}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleEdit}
            loading={saving}
            leftIcon={<Edit2 size={14} />}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteDept}
        onClose={() => setDeleteDept(null)}
        title="Deactivate Department"
        size="sm"
      >
        {deleteDept && (
          <>
            <p className="text-sm text-slate-600">
              Deactivate <strong>{deleteDept.name}</strong> ({deleteDept.code})?
              It will no longer appear in assessment department dropdowns. Past
              submissions keep their recorded department. You can reactivate it
              later.
            </p>
            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setDeleteDept(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleDeactivate}
                loading={deleting}
              >
                Deactivate
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!purgeDept}
        onClose={() => setPurgeDept(null)}
        title="Delete Department Permanently"
        size="sm"
      >
        {purgeDept && (
          <>
            <p className="text-sm text-slate-600">
              Permanently delete <strong>{purgeDept.name}</strong> (
              {purgeDept.code})? This cannot be undone. The department will be
              removed from your organisation; users assigned to it will be
              unlinked. Past assessment submissions are kept with their recorded
              department name.
            </p>
            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setPurgeDept(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handlePermanentDelete}
                loading={purging}
                leftIcon={<Trash2 size={14} />}
              >
                Delete Permanently
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
